import { CheckCircle2, AlertCircle, FileText, Users, MessageSquare } from 'lucide-react'
import type { SimulationReviewAuditInfo } from '../types'

type AuditSummaryBannerProps = {
  auditInfo: SimulationReviewAuditInfo
  onFilterMissing?: () => void
}

export function AuditSummaryBanner({ auditInfo, onFilterMissing }: AuditSummaryBannerProps) {
  const hasIssues = auditInfo.actionsWithoutMeasures > 0
  const measureCoverage =
    auditInfo.totalActions > 0
      ? Math.round((auditInfo.actionsWithMeasures / auditInfo.totalActions) * 100)
      : 0

  return (
    <div
      className={`border-b ${
        hasIssues ? 'bg-yellow-50 border-yellow-200' : 'bg-green-50 border-green-200'
      }`}
    >
      <div className="px-4 py-3">
        <div className="flex items-center justify-between flex-wrap gap-4">
          {/* Status indicator */}
          <div className="flex items-center gap-2">
            {hasIssues ? (
              <>
                <AlertCircle className="w-5 h-5 text-yellow-600" />
                <span className="font-medium text-yellow-800">
                  Review needed: {auditInfo.actionsWithoutMeasures} actions missing measures
                </span>
              </>
            ) : (
              <>
                <CheckCircle2 className="w-5 h-5 text-green-600" />
                <span className="font-medium text-green-800">
                  All actions have measures assigned
                </span>
              </>
            )}
          </div>

          {/* Stats */}
          <div className="flex items-center gap-6 text-sm">
            <div className="flex items-center gap-1.5">
              <FileText className="w-4 h-4 text-gray-500" />
              <span className="text-gray-700">
                <strong>{auditInfo.totalActions}</strong> actions
              </span>
              {auditInfo.totalActions > 0 && (
                <span className="text-gray-500">
                  ({measureCoverage}% covered)
                </span>
              )}
            </div>

            <div className="flex items-center gap-1.5">
              <MessageSquare className="w-4 h-4 text-gray-500" />
              <span className="text-gray-700">
                <strong>{auditInfo.totalMessages}</strong> messages
              </span>
            </div>

            <div className="flex items-center gap-1.5">
              <Users className="w-4 h-4 text-gray-500" />
              <span className="text-gray-700">
                <strong>{auditInfo.totalStakeholders}</strong> stakeholders
              </span>
            </div>
          </div>

          {/* Action button */}
          {hasIssues && onFilterMissing && (
            <button
              onClick={onFilterMissing}
              className="px-3 py-1.5 text-sm font-medium text-yellow-700 bg-yellow-100 hover:bg-yellow-200 rounded-md transition-colors"
            >
              Show missing
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
